import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { medicalDocumentsAPI, familyMembersAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Heart,
  AlertTriangle,
  Pill,
  History,
  Phone,
  User,
  Droplets,
  Edit3,
  Save,
  X,
  Plus,
  Stethoscope,
  FlaskConical,
  Scan,
  Syringe,
  FileText,
  Calendar,
  ChevronRight,
  Loader2,
  Eye,
  Download,
  Upload,
  Users,
  Clock,
  Shield,
  Share2,
  Link,
  Copy,
  Trash2,
  CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const BLOOD_TYPES = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'];

const MedicalRecordsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State
  const [loading, setLoading] = useState(true);
  const [profile, setProfile] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [groupedTimeline, setGroupedTimeline] = useState({});
  const [familyMembers, setFamilyMembers] = useState([]);
  const [selectedMember, setSelectedMember] = useState('me');
  const [activeTab, setActiveTab] = useState('timeline');
  
  // Edit mode
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    blood_type: '',
    allergies: [],
    known_conditions: [],
    current_treatments: [],
    medical_history: [],
    emergency_contact_name: '',
    emergency_contact_phone: '',
    emergency_contact_relationship: ''
  });
  const [newAllergy, setNewAllergy] = useState('');
  const [newCondition, setNewCondition] = useState('');
  const [newTreatment, setNewTreatment] = useState('');
  const [newHistory, setNewHistory] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  
  // Document view
  const [showDocModal, setShowDocModal] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(null);
  
  // PDF export
  const [exportingPDF, setExportingPDF] = useState(false);
  
  // Sharing
  const [showShareModal, setShowShareModal] = useState(false);
  const [shares, setShares] = useState([]);
  const [creatingShare, setCreatingShare] = useState(false);
  const [shareExpiry, setShareExpiry] = useState('48');
  const [newShareLink, setNewShareLink] = useState(null);
  const [copiedLink, setCopiedLink] = useState(false);

  // Load data
  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // Load profile
      const profileRes = await medicalDocumentsAPI.getProfile();
      setProfile(profileRes.data);
      
      // Load timeline
      const timelineRes = await medicalDocumentsAPI.getTimeline(
        selectedMember !== 'me' ? selectedMember : null
      );
      setTimeline(timelineRes.data.timeline || []);
      setGroupedTimeline(timelineRes.data.grouped || {});
      
      // Load family members
      const familyRes = await familyMembersAPI.getAll();
      setFamilyMembers(familyRes.data.members || []);
      
      // Load active shares
      try {
        const sharesRes = await medicalDocumentsAPI.getMyShares();
        setShares(sharesRes.data.shares || []);
      } catch (e) {
        // Ignore if no shares
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  }, [selectedMember]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Edit profile
  const startEditProfile = () => {
    if (profile) {
      setProfileForm({
        blood_type: profile.blood_type || '',
        allergies: profile.allergies || [],
        known_conditions: profile.known_conditions || [],
        current_treatments: profile.current_treatments || [],
        medical_history: profile.medical_history || [],
        emergency_contact_name: profile.emergency_contact?.name || '',
        emergency_contact_phone: profile.emergency_contact?.phone || '',
        emergency_contact_relationship: profile.emergency_contact?.relationship || ''
      });
    }
    setEditingProfile(true);
  };

  const saveProfile = async () => {
    setSavingProfile(true);
    try {
      const params = {
        blood_type: profileForm.blood_type || null,
        allergies: JSON.stringify(profileForm.allergies),
        known_conditions: JSON.stringify(profileForm.known_conditions),
        current_treatments: JSON.stringify(profileForm.current_treatments),
        medical_history: JSON.stringify(profileForm.medical_history),
        emergency_contact_name: profileForm.emergency_contact_name || null,
        emergency_contact_phone: profileForm.emergency_contact_phone || null,
        emergency_contact_relationship: profileForm.emergency_contact_relationship || null
      };
      
      await medicalDocumentsAPI.updateProfile(params);
      toast.success('Profil médical mis à jour');
      setEditingProfile(false);
      loadData();
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingProfile(false);
    }
  };

  // Add item to array
  const addToArray = (field, value, setter) => {
    if (value.trim()) {
      setProfileForm(prev => ({
        ...prev,
        [field]: [...prev[field], value.trim()]
      }));
      setter('');
    }
  };

  // Export PDF handler
  const handleExportPDF = async () => {
    setExportingPDF(true);
    try {
      const response = await medicalDocumentsAPI.exportProfilePDF();
      
      // Create blob from response
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = `DOCKHTA_Dossier_Medical_${user?.first_name || 'Patient'}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Cleanup
      window.URL.revokeObjectURL(url);
      
      toast.success('Dossier médical exporté avec succès');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Erreur lors de l\'export du PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  // Create share link
  const handleCreateShare = async () => {
    setCreatingShare(true);
    try {
      const response = await medicalDocumentsAPI.createShareLink(parseInt(shareExpiry));
      const baseUrl = window.location.origin;
      const shareUrl = `${baseUrl}${response.data.share_url}`;
      setNewShareLink({
        ...response.data,
        full_url: shareUrl
      });
      
      // Reload shares
      const sharesRes = await medicalDocumentsAPI.getMyShares();
      setShares(sharesRes.data.shares || []);
      
      toast.success('Lien de partage créé');
    } catch (error) {
      console.error('Error creating share:', error);
      toast.error('Erreur lors de la création du lien');
    } finally {
      setCreatingShare(false);
    }
  };

  // Revoke share link
  const handleRevokeShare = async (shareId) => {
    try {
      await medicalDocumentsAPI.revokeShare(shareId);
      setShares(prev => prev.filter(s => s.id !== shareId));
      toast.success('Lien révoqué');
    } catch (error) {
      console.error('Error revoking share:', error);
      toast.error('Erreur lors de la révocation');
    }
  };

  // Copy link to clipboard
  const copyToClipboard = (url) => {
    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    toast.success('Lien copié !');
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const removeFromArray = (field, index) => {
    setProfileForm(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  // Get icon for event type
  const getEventIcon = (event) => {
    const iconMap = {
      'stethoscope': Stethoscope,
      'flask': FlaskConical,
      'scan': Scan,
      'syringe': Syringe,
      'file-text': FileText,
      'file': FileText
    };
    return iconMap[event.icon] || FileText;
  };

  const getColorClasses = (color) => {
    const colors = {
      'blue': 'bg-blue-100 text-blue-600 border-blue-200',
      'green': 'bg-green-100 text-green-600 border-green-200',
      'purple': 'bg-purple-100 text-purple-600 border-purple-200',
      'orange': 'bg-orange-100 text-orange-600 border-orange-200',
      'indigo': 'bg-indigo-100 text-indigo-600 border-indigo-200',
      'slate': 'bg-slate-100 text-slate-600 border-slate-200'
    };
    return colors[color] || colors.slate;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#E53935]" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 pb-24" data-testid="medical-records-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ma Santé
          </h1>
          <p className="text-sm text-slate-500">Votre dossier médical simplifié</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowShareModal(true)}
            variant="outline"
            className="rounded-xl border-green-600 text-green-600 hover:bg-green-50"
            data-testid="share-btn"
          >
            <Share2 className="w-4 h-4 mr-2" />
            Partager
          </Button>
          <Button
            onClick={handleExportPDF}
            variant="outline"
            className="rounded-xl border-[#1976D2] text-[#1976D2] hover:bg-[#1976D2]/10"
            disabled={exportingPDF}
            data-testid="export-pdf-btn"
          >
            {exportingPDF ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Download className="w-4 h-4 mr-2" />
            )}
            Exporter PDF
          </Button>
          <Button
            onClick={() => navigate('/patient/medical-records/upload')}
            className="bg-[#E53935] hover:bg-[#C62828] rounded-xl"
            data-testid="add-document-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Ajouter
          </Button>
        </div>
      </div>

      {/* Family member selector */}
      {familyMembers.length > 0 && (
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          <Button
            variant={selectedMember === 'me' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setSelectedMember('me')}
            className={`rounded-full ${selectedMember === 'me' ? 'bg-[#1976D2]' : ''}`}
          >
            <User className="w-4 h-4 mr-1" />
            Moi
          </Button>
          {familyMembers.map(member => (
            <Button
              key={member.id}
              variant={selectedMember === member.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedMember(member.id)}
              className={`rounded-full ${selectedMember === member.id ? 'bg-[#1976D2]' : ''}`}
            >
              <Users className="w-4 h-4 mr-1" />
              {member.first_name}
            </Button>
          ))}
        </div>
      )}

      {/* ==================== INFORMATIONS IMPORTANTES ==================== */}
      <Card className="mb-6 border-0 shadow-lg bg-gradient-to-br from-white to-slate-50" data-testid="medical-summary-card">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-semibold text-slate-900 flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#E53935]" />
              Informations importantes
            </h2>
            {!editingProfile ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={startEditProfile}
                className="text-[#1976D2]"
                data-testid="edit-profile-btn"
              >
                <Edit3 className="w-4 h-4 mr-1" />
                Modifier
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setEditingProfile(false)}
                >
                  <X className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  onClick={saveProfile}
                  disabled={savingProfile}
                  className="bg-[#059669] hover:bg-[#047857]"
                  data-testid="save-profile-btn"
                >
                  {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
                  Enregistrer
                </Button>
              </div>
            )}
          </div>

          {editingProfile ? (
            /* ===== EDIT MODE ===== */
            <div className="space-y-4">
              {/* Blood Type */}
              <div>
                <Label className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                  <Droplets className="w-3 h-3" /> Groupe sanguin
                </Label>
                <Select
                  value={profileForm.blood_type}
                  onValueChange={(v) => setProfileForm(p => ({ ...p, blood_type: v }))}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue placeholder="Sélectionner" />
                  </SelectTrigger>
                  <SelectContent>
                    {BLOOD_TYPES.map(bt => (
                      <SelectItem key={bt} value={bt}>{bt}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Allergies */}
              <div>
                <Label className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                  <AlertTriangle className="w-3 h-3" /> Allergies
                </Label>
                <div className="flex flex-wrap gap-1 mb-2">
                  {profileForm.allergies.map((a, i) => (
                    <Badge key={i} variant="outline" className="bg-red-50 text-red-700 border-red-200">
                      {a}
                      <button onClick={() => removeFromArray('allergies', i)} className="ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newAllergy}
                    onChange={(e) => setNewAllergy(e.target.value)}
                    placeholder="Ajouter une allergie"
                    className="h-9"
                    onKeyPress={(e) => e.key === 'Enter' && addToArray('allergies', newAllergy, setNewAllergy)}
                  />
                  <Button size="sm" variant="outline" onClick={() => addToArray('allergies', newAllergy, setNewAllergy)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Known Conditions */}
              <div>
                <Label className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                  <Heart className="w-3 h-3" /> Maladies connues
                </Label>
                <div className="flex flex-wrap gap-1 mb-2">
                  {profileForm.known_conditions.map((c, i) => (
                    <Badge key={i} variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                      {c}
                      <button onClick={() => removeFromArray('known_conditions', i)} className="ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newCondition}
                    onChange={(e) => setNewCondition(e.target.value)}
                    placeholder="Ex: Diabète, Hypertension..."
                    className="h-9"
                    onKeyPress={(e) => e.key === 'Enter' && addToArray('known_conditions', newCondition, setNewCondition)}
                  />
                  <Button size="sm" variant="outline" onClick={() => addToArray('known_conditions', newCondition, setNewCondition)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Current Treatments */}
              <div>
                <Label className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                  <Pill className="w-3 h-3" /> Traitements en cours
                </Label>
                <div className="flex flex-wrap gap-1 mb-2">
                  {profileForm.current_treatments.map((t, i) => (
                    <Badge key={i} variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                      {t}
                      <button onClick={() => removeFromArray('current_treatments', i)} className="ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newTreatment}
                    onChange={(e) => setNewTreatment(e.target.value)}
                    placeholder="Ex: Metformine 500mg..."
                    className="h-9"
                    onKeyPress={(e) => e.key === 'Enter' && addToArray('current_treatments', newTreatment, setNewTreatment)}
                  />
                  <Button size="sm" variant="outline" onClick={() => addToArray('current_treatments', newTreatment, setNewTreatment)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Medical History */}
              <div>
                <Label className="text-xs text-slate-500 flex items-center gap-1 mb-1">
                  <History className="w-3 h-3" /> Antécédents
                </Label>
                <div className="flex flex-wrap gap-1 mb-2">
                  {profileForm.medical_history.map((h, i) => (
                    <Badge key={i} variant="outline" className="bg-slate-100 text-slate-700 border-slate-200">
                      {h}
                      <button onClick={() => removeFromArray('medical_history', i)} className="ml-1">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={newHistory}
                    onChange={(e) => setNewHistory(e.target.value)}
                    placeholder="Ex: Appendicectomie (2019)..."
                    className="h-9"
                    onKeyPress={(e) => e.key === 'Enter' && addToArray('medical_history', newHistory, setNewHistory)}
                  />
                  <Button size="sm" variant="outline" onClick={() => addToArray('medical_history', newHistory, setNewHistory)}>
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
              </div>

              {/* Emergency Contact */}
              <div>
                <Label className="text-xs text-slate-500 flex items-center gap-1 mb-2">
                  <Phone className="w-3 h-3" /> Contact d'urgence
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  <Input
                    value={profileForm.emergency_contact_name}
                    onChange={(e) => setProfileForm(p => ({ ...p, emergency_contact_name: e.target.value }))}
                    placeholder="Nom"
                    className="h-9"
                  />
                  <Input
                    value={profileForm.emergency_contact_phone}
                    onChange={(e) => setProfileForm(p => ({ ...p, emergency_contact_phone: e.target.value }))}
                    placeholder="Téléphone"
                    className="h-9"
                  />
                  <Input
                    value={profileForm.emergency_contact_relationship}
                    onChange={(e) => setProfileForm(p => ({ ...p, emergency_contact_relationship: e.target.value }))}
                    placeholder="Lien (Époux, Parent...)"
                    className="h-9"
                  />
                </div>
              </div>
            </div>
          ) : (
            /* ===== VIEW MODE ===== */
            <div className="grid grid-cols-2 gap-3 text-sm">
              {/* Blood Type */}
              <div className="flex items-start gap-2">
                <Droplets className="w-4 h-4 text-red-500 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Groupe sanguin</p>
                  <p className="font-medium text-slate-900">
                    {profile?.blood_type || <span className="text-slate-400">Non renseigné</span>}
                  </p>
                </div>
              </div>

              {/* Allergies */}
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Allergies</p>
                  {profile?.allergies?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {profile.allergies.map((a, i) => (
                        <Badge key={i} variant="outline" className="text-xs bg-red-50 text-red-700 border-red-200">
                          {a}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-slate-400">Aucune</p>
                  )}
                </div>
              </div>

              {/* Known Conditions */}
              <div className="flex items-start gap-2">
                <Heart className="w-4 h-4 text-purple-500 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Maladies connues</p>
                  {profile?.known_conditions?.length > 0 ? (
                    <p className="font-medium text-slate-900">{profile.known_conditions.join(', ')}</p>
                  ) : (
                    <p className="text-slate-400">Aucune</p>
                  )}
                </div>
              </div>

              {/* Current Treatments */}
              <div className="flex items-start gap-2">
                <Pill className="w-4 h-4 text-blue-500 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Traitements actuels</p>
                  {profile?.current_treatments?.length > 0 ? (
                    <p className="font-medium text-slate-900">{profile.current_treatments.join(', ')}</p>
                  ) : (
                    <p className="text-slate-400">Aucun</p>
                  )}
                </div>
              </div>

              {/* Medical History */}
              <div className="flex items-start gap-2">
                <History className="w-4 h-4 text-slate-500 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Antécédents</p>
                  {profile?.medical_history?.length > 0 ? (
                    <p className="font-medium text-slate-900">{profile.medical_history.join(', ')}</p>
                  ) : (
                    <p className="text-slate-400">Aucun</p>
                  )}
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="flex items-start gap-2">
                <Phone className="w-4 h-4 text-green-500 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Contact d'urgence</p>
                  {profile?.emergency_contact?.name ? (
                    <p className="font-medium text-slate-900">
                      {profile.emergency_contact.name}
                      {profile.emergency_contact.relationship && (
                        <span className="text-slate-500 text-xs"> ({profile.emergency_contact.relationship})</span>
                      )}
                      {profile.emergency_contact.phone && (
                        <span className="block text-xs text-slate-600">{profile.emergency_contact.phone}</span>
                      )}
                    </p>
                  ) : (
                    <p className="text-slate-400">Non renseigné</p>
                  )}
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ==================== TIMELINE ==================== */}
      <div className="mb-4">
        <h2 className="font-semibold text-slate-900 flex items-center gap-2 mb-4">
          <Calendar className="w-5 h-5 text-[#1976D2]" />
          Mon historique santé
        </h2>

        {Object.keys(groupedTimeline).length === 0 ? (
          <Card className="border border-dashed">
            <CardContent className="py-12 text-center">
              <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600 font-medium">Aucun événement</p>
              <p className="text-sm text-slate-500 mt-1">
                Vos consultations et documents apparaîtront ici
              </p>
              <Button
                onClick={() => navigate('/search')}
                className="mt-4 bg-[#E53935] hover:bg-[#C62828] rounded-xl"
              >
                Prendre un rendez-vous
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {Object.entries(groupedTimeline).map(([period, events]) => (
              <div key={period}>
                {/* Period header */}
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-semibold text-slate-700">{period}</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>

                {/* Events */}
                <div className="space-y-3">
                  {events.map((event) => {
                    const Icon = getEventIcon(event);
                    const colorClasses = getColorClasses(event.color);
                    
                    return (
                      <Card
                        key={event.id}
                        className="border-0 shadow-sm hover:shadow-md transition-all cursor-pointer"
                        onClick={() => {
                          if (event.details?.file_url) {
                            setSelectedDoc(event);
                            setShowDocModal(true);
                          }
                        }}
                        data-testid={`timeline-event-${event.id}`}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            {/* Icon */}
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${colorClasses}`}>
                              <Icon className="w-5 h-5" />
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-start justify-between gap-2">
                                <div>
                                  <p className="font-medium text-slate-900">{event.title}</p>
                                  <p className="text-sm text-slate-500">{event.subtitle}</p>
                                </div>
                                {event.details?.file_url && (
                                  <Button variant="ghost" size="sm" className="text-[#1976D2]">
                                    <Eye className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>

                              {/* Details */}
                              {event.details?.diagnosis && (
                                <p className="text-sm text-slate-600 mt-2 bg-slate-50 p-2 rounded-lg">
                                  <span className="font-medium">Diagnostic :</span> {event.details.diagnosis}
                                </p>
                              )}
                              {event.details?.notes && (
                                <p className="text-sm text-slate-600 mt-2">
                                  {event.details.notes}
                                </p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Document view modal */}
      <Dialog open={showDocModal} onOpenChange={setShowDocModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{selectedDoc?.title}</DialogTitle>
            <DialogDescription>{selectedDoc?.subtitle}</DialogDescription>
          </DialogHeader>
          
          {selectedDoc?.details?.file_url && (
            <div className="mt-4">
              {selectedDoc.details.file_url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                <img
                  src={selectedDoc.details.file_url}
                  alt={selectedDoc.title}
                  className="w-full rounded-lg"
                />
              ) : (
                <div className="bg-slate-100 rounded-lg p-8 text-center">
                  <FileText className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                  <p className="text-slate-600 mb-4">Document PDF</p>
                  <Button
                    onClick={() => window.open(selectedDoc.details.file_url, '_blank')}
                    className="bg-[#1976D2] hover:bg-[#1565C0]"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Télécharger
                  </Button>
                </div>
              )}
            </div>
          )}

          {selectedDoc?.details?.description && (
            <p className="text-sm text-slate-600 mt-4">
              {selectedDoc.details.description}
            </p>
          )}
        </DialogContent>
      </Dialog>

      {/* Share Modal */}
      <Dialog open={showShareModal} onOpenChange={setShowShareModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 className="w-5 h-5 text-green-600" />
              Partager ma synthèse médicale
            </DialogTitle>
            <DialogDescription>
              Créez un lien temporaire et sécurisé pour partager votre dossier avec un professionnel de santé.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* New share form */}
            {!newShareLink ? (
              <Card className="border-green-100 bg-green-50/50">
                <CardContent className="p-4">
                  <Label className="text-sm font-medium text-slate-700">
                    Durée de validité
                  </Label>
                  <Select value={shareExpiry} onValueChange={setShareExpiry}>
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="24">24 heures</SelectItem>
                      <SelectItem value="48">48 heures</SelectItem>
                      <SelectItem value="72">3 jours</SelectItem>
                      <SelectItem value="168">7 jours</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-2">
                    Le lien sera automatiquement désactivé après cette période ou après 10 consultations.
                  </p>
                  <Button
                    onClick={handleCreateShare}
                    disabled={creatingShare}
                    className="w-full mt-4 bg-green-600 hover:bg-green-700"
                    data-testid="create-share-btn"
                  >
                    {creatingShare ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Link className="w-4 h-4 mr-2" />
                    )}
                    Créer un lien de partage
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-green-200 bg-green-50">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-800">Lien créé !</span>
                  </div>
                  <div className="bg-white rounded-lg p-3 border border-green-200">
                    <p className="text-xs text-slate-500 mb-1">Copiez ce lien :</p>
                    <div className="flex items-center gap-2">
                      <Input
                        value={newShareLink.full_url}
                        readOnly
                        className="text-xs bg-slate-50"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(newShareLink.full_url)}
                        className={copiedLink ? 'bg-green-100 border-green-300' : ''}
                      >
                        {copiedLink ? <CheckCircle className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                      </Button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    Expire le {new Date(newShareLink.expires_at).toLocaleDateString('fr-FR')} à {new Date(newShareLink.expires_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                  </p>
                  <Button
                    variant="link"
                    size="sm"
                    onClick={() => setNewShareLink(null)}
                    className="mt-2 p-0 h-auto text-slate-500"
                  >
                    Créer un autre lien
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Active shares list */}
            {shares.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-slate-700 mb-2">Liens actifs ({shares.length})</h4>
                <div className="space-y-2 max-h-40 overflow-y-auto">
                  {shares.map((share) => (
                    <div
                      key={share.id}
                      className="flex items-center justify-between bg-slate-50 rounded-lg p-3 text-sm"
                    >
                      <div>
                        <p className="text-slate-600">
                          Expire le {new Date(share.expires_at).toLocaleDateString('fr-FR')}
                        </p>
                        <p className="text-xs text-slate-400">
                          {share.access_count || 0} consultation(s)
                        </p>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => copyToClipboard(`${window.location.origin}/shared/medical/${share.token}`)}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleRevokeShare(share.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MedicalRecordsPage;
